// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import { IRfid, IOcppChargingSession } from "states/ocppstates";
import { isSet } from "shared/utils/utils";

import cssContent from "constants.module.scss";

export enum RfidStatus {
  AVAILABLE,
  CHARGING,
  EXPIRED,
}

class Rfid {
  public rfid;
  public pendingSession;

  public static getStatusColor(status: RfidStatus): string {
    switch (status) {
      case RfidStatus.AVAILABLE:
        return cssContent.CHARGER_COLOR_AVAILABLE;
      case RfidStatus.CHARGING:
        return cssContent.CHARGER_COLOR_CHARGING;
      case RfidStatus.EXPIRED:
        return cssContent.CHARGER_COLOR_FAULTED;
    }
    return cssContent.CHARGER_COLOR_UNDEFINED;
  }

  constructor(rfid: IRfid, pendingSession?: IOcppChargingSession | undefined) {
    this.rfid = rfid;
    this.pendingSession = pendingSession;
  }

  public isExpired() {
    // check limits
    let expired = false; // default
    if (this.rfid.limitStart && this.rfid.limitStart > Date.now() / 1000) {
      expired = true;
    } else if (this.rfid.limitEnd && this.rfid.limitEnd < Date.now() / 1000) {
      expired = true;
    } else if (this.rfid.limitEnergy) {
      // get used energy
      if (this.rfid.limitEnergy < (this.rfid.usedEnergy ?? 0)) {
        expired = true;
      }
    } else if (this.rfid.limitCost) {
      // get used energy
      if (this.rfid.limitCost < (this.rfid.usedCost ?? 0)) {
        expired = true;
      }
    }
    return expired;
  }

  // check whether all space separated filter texts match card number, name or category
  public matchesFilter(filterText: string) {
    return filterText
      .toLocaleLowerCase()
      .split(" ")
      .every(
        (curFilter) =>
          curFilter === "" ||
          this.rfid.rfid.toLocaleLowerCase().includes(curFilter) ||
          this.rfid.name?.toLocaleLowerCase().includes(curFilter) ||
          this.rfid.category?.toLocaleLowerCase().includes(curFilter),
      );
  }

  public getStatus(): RfidStatus {
    const status =
      isSet(this.pendingSession) ? RfidStatus.CHARGING
      : this.isExpired() ? RfidStatus.EXPIRED
      : RfidStatus.AVAILABLE;
    return status;
  }

  public getColor(): string {
    const status = this.getStatus();
    return Rfid.getStatusColor(status);
  }
}

export default Rfid;
